import { TRPCClientError } from "@trpc/client";
import type { TRPC_ERROR_CODE_KEY } from "@trpc/server/rpc";
import {
  MONTHLY_PROVIDER_REVEAL_LIMIT_MESSAGE,
  ProviderRevealErrorCode,
} from "@api/shared-runtime/provider-reveal-errors";

export enum RevealProviderFailure {
  LimitReached = "limit-reached",
  NoPermission = "no-permission",
  ProviderNotInBusinessUnit = "provider-not-in-business-unit",
  Unknown = "unknown",
}

const BAD_REQUEST_CODE: TRPC_ERROR_CODE_KEY = "BAD_REQUEST";
const FORBIDDEN_CODE: TRPC_ERROR_CODE_KEY = "FORBIDDEN";

export const REVEAL_PROVIDER_FAILURE_MESSAGES: Record<
  Exclude<RevealProviderFailure, RevealProviderFailure.LimitReached>,
  string
> = {
  [RevealProviderFailure.NoPermission]:
    "You don't have permission to reveal providers in this business unit. Ask a business unit admin to reveal this provider or to update your role.",
  [RevealProviderFailure.ProviderNotInBusinessUnit]:
    "This provider isn't sharing their profile with your business unit, so they can't be revealed.",
  [RevealProviderFailure.Unknown]:
    "We couldn't reveal this provider right now. Please try again, or contact Saile if the problem continues.",
};

type StructuredCause = { code?: string; limit?: number };

type ParsedRevealProviderError = {
  failure: RevealProviderFailure;
  monthlyLimit: number | null;
};

// Server messages are never surfaced to the user from here: in development
// FORBIDDEN carries the missing permission name and INTERNAL_SERVER_ERROR
// carries the raw exception text.
export function parseRevealProviderError(
  error: unknown,
): ParsedRevealProviderError {
  if (!(error instanceof TRPCClientError)) {
    return { failure: RevealProviderFailure.Unknown, monthlyLimit: null };
  }

  const data = error.data as
    | { code?: string; cause?: StructuredCause }
    | undefined;
  const cause = data?.cause;
  const isLimitReached =
    cause?.code === ProviderRevealErrorCode.MonthlyLimitReached ||
    (data?.code === BAD_REQUEST_CODE &&
      error.message === MONTHLY_PROVIDER_REVEAL_LIMIT_MESSAGE);
  if (isLimitReached) {
    return {
      failure: RevealProviderFailure.LimitReached,
      monthlyLimit: cause?.limit ?? null,
    };
  }

  if (
    cause?.code === ProviderRevealErrorCode.ProviderNotSharedWithBusinessUnit
  ) {
    return {
      failure: RevealProviderFailure.ProviderNotInBusinessUnit,
      monthlyLimit: null,
    };
  }

  if (data?.code === FORBIDDEN_CODE) {
    return { failure: RevealProviderFailure.NoPermission, monthlyLimit: null };
  }

  return { failure: RevealProviderFailure.Unknown, monthlyLimit: null };
}
